import { z } from 'zod';
import { CEFR_LEVELS, type Cefr, type SpeechScores } from './scoring.ts';
import type { TaskKind } from './tasks.ts';

/**
 * Running picture of the learner across scored answers (memory.skills). Written only when an answer is scored; read by
 * the prompt (weak areas) and the Progress tab. Mirrors Stimuler's phoneme proficiency: an exponential moving average
 * with alpha 0.2, the first sample taken as-is (backend phoneme_group_scorer.py).
 */
export const EMA_ALPHA = 0.2;
export const CEFR_HISTORY = 10;

const ema = z.number().nullable().catch(null).default(null);
const counts = z.record(z.string(), z.number()).catch({}).default({});

// Lenient like the rest of memory: a shape from a newer build must not fail the whole document, and keys this build
// doesn't know (at the top level or on a phoneme entry) are kept and written back untouched.
export const skillsSchema = z.looseObject({
  samples: z.number().int().catch(0).default(0),
  overall: ema,
  pronunciation: ema,
  fluency: ema,
  grammar: ema,
  vocabulary: ema,
  wpm: ema,
  fillers: ema, // per answer
  pauses: ema, // long pauses per answer
  phonemes: z
    .record(z.string(), z.looseObject({ ema: z.number(), count: z.number().int() }))
    .catch({})
    .default({}),
  grammarErrors: counts, // edits per learner category, cumulative
  vocabCefr: counts, // content words per CEFR level, cumulative
  cefrHistory: z.array(z.enum(CEFR_LEVELS)).catch([]).default([]),
  // Answers already counted (`session:task`), so a resubmitted answer isn't counted twice.
  seen: z.array(z.string()).catch([]).default([]),
  updatedAt: z.string().nullable().catch(null).default(null),
});
export type Skills = z.infer<typeof skillsSchema>;

export function emptySkills(): Skills {
  return skillsSchema.parse({});
}

// Two decimals: enough precision for a 0-100 scale, and stored numbers stay readable.
const step = (prev: number | null, value: number | null | undefined) =>
  value === null || value === undefined
    ? prev
    : prev === null
      ? value
      : Math.round((EMA_ALPHA * value + (1 - EMA_ALPHA) * prev) * 100) / 100;

const add = (into: Record<string, number>, key: string, n: number) => ({ ...into, [key]: (into[key] ?? 0) + n });

export const SEEN_LIMIT = 100;

/**
 * Folds one scored answer into the running skills. Pure. A read-aloud line says a lot about pronunciation but little
 * about level (no grammar or vocabulary, and reading flatters fluency), so it feeds pronunciation and sounds only.
 */
export function addSample(
  skills: Skills,
  scores: Extract<SpeechScores, { status: 'scored' }>,
  at: string,
  opts: { kind?: TaskKind; key?: string } = {},
): Skills {
  if (opts.key && skills.seen.includes(opts.key)) return skills;
  const seen = opts.key ? [...skills.seen, opts.key].slice(-SEEN_LIMIT) : skills.seen;
  const phonemes = { ...skills.phonemes };
  for (const p of scores.phonemes) {
    const prev = phonemes[p.phoneme];
    phonemes[p.phoneme] = {
      ...prev,
      ema: step(prev?.ema ?? null, p.accuracy) ?? p.accuracy,
      count: (prev?.count ?? 0) + p.count,
    };
  }
  const pronunciation = step(skills.pronunciation, scores.pronunciation?.score);
  if (opts.kind === 'read_aloud') {
    return { ...skills, samples: skills.samples + 1, pronunciation, phonemes, seen, updatedAt: at };
  }
  let grammarErrors = skills.grammarErrors;
  for (const e of scores.grammar?.edits ?? []) grammarErrors = add(grammarErrors, e.category, 1);
  let vocabCefr = skills.vocabCefr;
  for (const [level, n] of Object.entries(scores.vocabulary?.cefr ?? {})) vocabCefr = add(vocabCefr, level, n);
  return {
    ...skills,
    samples: skills.samples + 1,
    overall: step(skills.overall, scores.overall),
    pronunciation,
    fluency: step(skills.fluency, scores.fluency?.score),
    grammar: step(skills.grammar, scores.grammar?.score),
    vocabulary: step(skills.vocabulary, scores.vocabulary?.score),
    wpm: step(skills.wpm, scores.fluency?.wpm),
    fillers: step(skills.fillers, scores.fluency?.fillers.length),
    pauses: step(skills.pauses, scores.fluency?.pauses),
    phonemes,
    grammarErrors,
    vocabCefr,
    cefrHistory: scores.cefr ? [...skills.cefrHistory, scores.cefr].slice(-CEFR_HISTORY) : skills.cefrHistory,
    seen,
    updatedAt: at,
  };
}

// One answer is one sample of one kind of speech: a level from it is a guess (ADR-0012). Read-aloud lines don't count
// (they carry no level), so these are free speech and interview answers.
export const MIN_LEVEL_SAMPLES = 2;

/**
 * The learner's level: the most common level across recent answers; on a tie, the lower median of the tied levels.
 * Null until MIN_LEVEL_SAMPLES unscripted answers were scored.
 */
export function overallCefr(skills: Skills): Cefr | null {
  if (skills.cefrHistory.length < MIN_LEVEL_SAMPLES) return null;
  const freq = new Map<Cefr, number>();
  for (const c of skills.cefrHistory) freq.set(c, (freq.get(c) ?? 0) + 1);
  const top = Math.max(...freq.values());
  const tied = CEFR_LEVELS.filter((c) => freq.get(c) === top);
  return tied[Math.floor((tied.length - 1) / 2)] ?? null;
}

/** Sounds to work on: below 70 and heard at least 3 times across answers (fewer is noise), weakest first. */
export function weakPhonemes(skills: Skills, limit = 5) {
  return Object.entries(skills.phonemes)
    .filter(([, p]) => p.ema < 70 && p.count >= 3)
    .sort(([, a], [, b]) => a.ema - b.ema)
    .slice(0, limit)
    .map(([phoneme, p]) => ({ phoneme, score: Math.round(p.ema), count: p.count }));
}

/** Grammar categories corrected at least twice, most frequent first. */
export function grammarFocus(skills: Skills, limit = 2) {
  return Object.entries(skills.grammarErrors)
    .filter(([, n]) => n >= 2) // one correction is a slip, not a pattern
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([category]) => category);
}
